import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { X, Award, TrendingUp, Percent } from "lucide-react";

const STORAGE_KEY = "prime_rate";

const PrimeModal = ({ employeeName, totalHT, totalTTC, onClose, isDarkMode }) => {
    const { t } = useTranslation("activite");
    const [rate, setRate] = useState(() => localStorage.getItem(STORAGE_KEY) ?? "");
    const inputRef = useRef(null);

    const baseHT = Number(totalHT || 0);
    const baseTTC = Number(totalTTC || 0);
    const parsedRate = parseFloat(rate);
    const validRate = !isNaN(parsedRate) && parsedRate >= 0 && parsedRate <= 100;
    const primeHT = validRate ? baseHT * (parsedRate / 100) : null;
    const primeTTC = validRate ? baseTTC * (parsedRate / 100) : null;

    useEffect(() => {
        inputRef.current?.focus();
        const handleKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", handleKey);
        return () => document.removeEventListener("keydown", handleKey);
    }, [onClose]);

    const formatAmount = (val) =>
        val.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " DH";

    const handleRateChange = (e) => {
        const v = e.target.value;
        if (v === "" || /^\d*\.?\d*$/.test(v)) {
            setRate(v);
            if (v === "") localStorage.removeItem(STORAGE_KEY);
            else localStorage.setItem(STORAGE_KEY, v);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

            {/* Modal */}
            <div
                className={`relative w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden ${
                    isDarkMode ? "bg-gray-800 text-gray-100" : "bg-white text-gray-900"
                }`}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-primary/30 bg-primary/10">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                            <Award className="w-4 h-4 text-white" />
                        </div>
                        <div>
                            <h3 className="text-sm font-semibold text-primary">{t("primeModal.title")}</h3>
                            <p className={`text-xs capitalize ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                                {employeeName || t("primeModal.defaultEmployee")}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className={`p-1.5 rounded-full transition-colors ${
                            isDarkMode ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-100 text-gray-500"
                        }`}
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="p-5 space-y-4">
                    {/* Chiffre d'affaires de référence */}
                    <div className={`rounded-xl overflow-hidden border ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
                        <p className={`text-xs px-3.5 pt-3 pb-1.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                            {t("primeModal.periodRevenue")}
                        </p>
                        <div className={`grid grid-cols-2 divide-x ${isDarkMode ? "divide-gray-700" : "divide-gray-200"}`}>
                            <div className="px-3.5 pb-3">
                                <p className={`text-[11px] mb-0.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>{t("primeModal.ht")}</p>
                                <p className={`text-sm font-bold ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}>{formatAmount(baseHT)}</p>
                            </div>
                            <div className="px-3.5 pb-3">
                                <p className={`text-[11px] mb-0.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>{t("primeModal.ttc")}</p>
                                <p className="text-sm font-bold text-primary">{formatAmount(baseTTC)}</p>
                            </div>
                        </div>
                    </div>

                    {/* Rate input */}
                    <div>
                        <label className={`block text-xs font-medium mb-1.5 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                            {t("primeModal.rateLabel")}
                        </label>
                        <div className="relative">
                            <input
                                ref={inputRef}
                                type="text"
                                inputMode="decimal"
                                value={rate}
                                onChange={handleRateChange}
                                placeholder="ex: 5"
                                className={`w-full rounded-lg px-4 py-2.5 pr-10 text-sm border outline-none transition-colors ${
                                    isDarkMode
                                        ? "bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-500 focus:border-primary"
                                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-primary"
                                } ${
                                    rate !== "" && !validRate
                                        ? "border-red-400 focus:border-red-400"
                                        : ""
                                }`}
                            />
                            <Percent className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`} />
                        </div>
                        {rate !== "" && !validRate && (
                            <p className="text-xs text-red-500 mt-1">
                                {t("primeModal.rateInvalid")}
                            </p>
                        )}
                    </div>

                    {/* Results */}
                    {validRate && primeHT !== null && primeTTC !== null && (
                        <div className={`rounded-xl overflow-hidden border ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
                            <div className={`px-4 py-2.5 flex items-center gap-1.5 ${isDarkMode ? "bg-gray-700/40" : "bg-gray-50"}`}>
                                <TrendingUp className="w-4 h-4 text-green-500" />
                                <span className={`text-xs font-medium ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                                    {t("primeModal.amountToPay", {rate: parsedRate})}
                                </span>
                            </div>
                            <div className={`grid grid-cols-2 divide-x ${isDarkMode ? "divide-gray-700" : "divide-gray-200"}`}>
                                <div className="p-3.5 text-center">
                                    <p className={`text-[11px] mb-0.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>{t("primeModal.onHtBasis")}</p>
                                    <p className="text-base font-bold text-green-600">{formatAmount(primeHT)}</p>
                                </div>
                                <div className="p-3.5 text-center">
                                    <p className={`text-[11px] mb-0.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>{t("primeModal.onTtcBasis")}</p>
                                    <p className="text-base font-bold text-green-600">{formatAmount(primeTTC)}</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PrimeModal;
